import { TrackItem } from '@/api/device/types';
import { HistoricalWaveDataItem } from '@/api/historicalData';
import { SPOTTER_SDK_TOKEN } from '@/api/oceansApi/constants';
import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { RequestOptions, axios } from '@/common/axios';
import { CombinedQueryResult, QueryConfig } from '@/common/react-query';
import { APIResponse } from '@/common/types';
import { OCEANS_WAVE_DATA_QUERY_KEY } from '@/config';
import { DateRange } from '@/store';

type GetOceansWaveDataResponse = APIResponse<{
    spotterId: string;
    track: TrackItem[];
    waves: HistoricalWaveDataItem[];
    wind: HistoricalWaveDataItem[];
}>;

export type OceansWaveSample = { series: 'waves' } & HistoricalWaveDataItem;
export type OceansTrackSample = { series: 'track' } & TrackItem;

/**
 * One request answers with both the wave samples and the track for a window, and the two hooks over an
 * oceans spotter want one each. Tagging them into a single series keeps that one request: both are samples
 * carrying the timestamp the window is cut on, so the windows reassemble the same way whichever tag a
 * sample has, and each caller narrows to its own with `isWaveSample`/`isTrackSample`.
 */
export type OceansSample = OceansWaveSample | OceansTrackSample;

export const isWaveSample = (sample: OceansSample): sample is OceansWaveSample => sample.series === 'waves';

export const isTrackSample = (sample: OceansSample): sample is OceansTrackSample => sample.series === 'track';

export type GetOceansWaveDataParams = {
    spotterId: string;
    startDate: string;
    endDate: string;
    includeWaves: boolean;
    includeTrack: boolean;
    includeWindData: boolean;
};

export const getOceansWaveData = async (
    params: GetOceansWaveDataParams,
    options?: RequestOptions
): Promise<OceansSample[]> => {
    const { data } = await axios.get<GetOceansWaveDataResponse, GetOceansWaveDataResponse>('/oceans-api/wave-data', {
        params,
        headers: { token: SPOTTER_SDK_TOKEN },
        ...options,
    });

    return [
        ...data.waves.map((wave): OceansWaveSample => ({ series: 'waves', ...wave })),
        ...data.track.map((item): OceansTrackSample => ({ series: 'track', ...item })),
    ];
};

type UseOceansWaveDataOptions = {
    spotterId?: string | null;
    dateRange?: DateRange | null;
    includeWaves?: boolean;
    includeTrack?: boolean;
    includeWindData?: boolean;
    config?: Omit<QueryConfig<typeof getOceansWaveData>, 'select'>;
};

export const useOceansWaveData = ({
    spotterId,
    dateRange,
    includeTrack = true,
    includeWaves = true,
    includeWindData = false,
    config,
}: UseOceansWaveDataOptions): CombinedQueryResult<OceansSample[]> => {
    const seriesRequested = { includeTrack, includeWaves, includeWindData };

    return useLiveSplitQuery({
        queryKeyPrefix: OCEANS_WAVE_DATA_QUERY_KEY,
        spotterId,
        dateRange,
        keyOptions: seriesRequested,
        fetchRange: ({ signal, viewToken: _viewToken, ...range }) =>
            getOceansWaveData({ ...range, ...seriesRequested }, { signal }),
        // oceans-api excludes the bounds it is given, like the other Spotter wave entrypoints
        apiDateBounds: 'exclusive',
        timestampKey: 'timestamp',
        sortOrder: 'asc',
        // two series concatenated, so the samples are not in timestamp order across the pair
        responseSortOrder: 'unsorted',
        config,
    });
};
